import { Mark, mergeAttributes } from "@tiptap/core";

/**
 * Superscript mark — `^…^` in LML, `\textsuperscript{…}` in LaTeX, `<sup>` in HTML.
 * Used for ordinals (1ˢᵗ), exponents in non-math text, footnote markers.
 */
export const Superscript = Mark.create({
  name: "superscript",
  parseHTML() {
    return [{ tag: "sup" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["sup", mergeAttributes(HTMLAttributes), 0];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-.": () => this.editor.commands.toggleMark("superscript"),
    };
  },
});

/**
 * Subscript mark — `%%…%%` in LML, `\textsubscript{…}` in LaTeX, `<sub>` in HTML.
 * Used for chemical formulas (H₂O), indices in non-math text.
 */
export const Subscript = Mark.create({
  name: "subscript",
  parseHTML() {
    return [{ tag: "sub" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["sub", mergeAttributes(HTMLAttributes), 0];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-,": () => this.editor.commands.toggleMark("subscript"),
    };
  },
});

/**
 * Highlight mark — `==…==` in LML, `\hl[color]{…}` (soul package) in LaTeX,
 * `<mark>` in HTML. Color attribute lets the FormatRailPanel "highlight"
 * picker (yellow / green / cyan / pink) toggle a colored variant.
 */
const HIGHLIGHT_COLORS: Record<string, string> = {
  yellow: "#fef08a", orange: "#fed7aa", lime: "#d9f99d", green: "#bbf7d0",
  cyan: "#a5f3fc", sky: "#bae6fd", blue: "#bfdbfe", violet: "#ddd6fe",
  pink: "#fbcfe8", rose: "#fecdd3", gray: "#e5e7eb",
};

export const Highlight = Mark.create({
  name: "highlight",
  addAttributes() {
    return {
      color: {
        default: "yellow",
        parseHTML: (el) => el.getAttribute("data-color") || "yellow",
        renderHTML: (attrs) => {
          const color = (attrs.color as string) || "yellow";
          const bg = HIGHLIGHT_COLORS[color] || HIGHLIGHT_COLORS.yellow;
          return {
            "data-color": color,
            style: `background-color: ${bg}; padding: 0 2px; border-radius: 2px;`,
          };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "mark" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["mark", mergeAttributes(HTMLAttributes), 0];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-Shift-h": () => this.editor.commands.toggleMark("highlight"),
    };
  },
});

/**
 * Text color mark — `\textcolor{name}{…}` in LaTeX, inline span with
 * `color:` style in HTML. 24-color palette aligned with the
 * FormatRailPanel COLOR picker and render-markers.ts. Unmapped values
 * pass through to CSS as-is (any xcolor / CSS color name works).
 */
const TEXT_COLORS: Record<string, string> = {
  red: "#ef4444", orange: "#f97316", amber: "#f59e0b", yellow: "#eab308",
  lime: "#84cc16", green: "#22c55e", emerald: "#10b981", teal: "#14b8a6",
  cyan: "#06b6d4", sky: "#0ea5e9", blue: "#3b82f6", indigo: "#6366f1",
  violet: "#8b5cf6", purple: "#a855f7", fuchsia: "#d946ef", pink: "#ec4899",
  rose: "#f43f5e", black: "#000000", gray: "#6b7280", darkgray: "#374151",
  lightgray: "#9ca3af", brown: "#92400e", olive: "#65a30d", navy: "#1e3a8a",
};

export const TextColor = Mark.create({
  name: "textColor",
  addAttributes() {
    return {
      color: {
        default: "red",
        parseHTML: (el) => el.getAttribute("data-color") || el.style.color || "red",
        renderHTML: (attrs) => {
          const c = (attrs.color as string) || "red";
          const resolved = TEXT_COLORS[c] || c;
          return { "data-color": c, style: `color: ${resolved};` };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-text-color]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { "data-text-color": "true" }), 0];
  },
});

/**
 * Comment mark — `[%…%]` in LML, `\iffalse … \fi` in LaTeX (block-comment
 * form that swallows arbitrary content while keeping it in the source),
 * `<span class="lml-comment">` in HTML preview. Visually rendered as
 * dim italicised strike-through so the writer sees what's been muted
 * without it disappearing from the page.
 *
 * Use case: select a sentence/word and "comment it out" the same way a
 * developer comments out a line of code — content stays in source, no
 * longer renders to PDF.
 */
export const Comment = Mark.create({
  name: "comment",
  parseHTML() {
    return [
      { tag: "span.lml-comment" },
      { tag: "span[data-comment]" },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        {
          class: "lml-comment",
          "data-comment": "true",
          style:
            "color: var(--lilia-text-muted, #9ca3af); text-decoration: line-through; font-style: italic; opacity: 0.75;",
        },
        HTMLAttributes,
      ),
      0,
    ];
  },
  addKeyboardShortcuts() {
    return {
      "Mod-/": () => this.editor.commands.toggleMark("comment"),
    };
  },
});

/**
 * Small-caps mark — `^^…^^` in LML, `\textsc{…}` in LaTeX, CSS `font-variant: small-caps`.
 * Used for acronyms (NASA → Nasa) and surnames in some academic styles.
 *
 * Note: `font-variant: small-caps` falls back to scaling when the font family
 * doesn't ship real small-caps glyphs. Charter/Inter both look acceptable.
 */
export const SmallCaps = Mark.create({
  name: "smallcaps",
  parseHTML() {
    return [
      { tag: "span[style*='small-caps']" },
      { tag: "span.smallcaps" },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(
        { class: "smallcaps", style: "font-variant: small-caps;" },
        HTMLAttributes
      ),
      0,
    ];
  },
});
